import React from 'react';
import moment from 'moment';
import { twMerge } from 'tailwind-merge';

import { useAppSelector } from 'flow/hooks/useRedux';
import ElapsedTime from 'flow/components/ui-components/ElapsedTime';
import Divider from 'flow/components/ui-components/Divider';
import useAppContext from 'flow/hooks/useAppContext';
import useSharedInterval from 'flow/hooks/useSharedInterval';
import { sliceUUIDFromUserName } from 'flow/utils/meeting-utils';
import { timeFormater } from 'flow/utils/date-time-utils';
import { selectLobbyMembers } from 'flow/selectors/meeting-selectors';
import MeetingLobbyOngoingAvatar from './MeetingLobbyOngoingAvatar';

const MAX_STACKED_AVATARS = 5;
const PLANNED_VS_COUNTDOWN_THRESHOLD_MINUTES = 15;
const COUNTDOWN_REFRESH_INTERVAL_MS = 30000;

const firstName = (displayName: string) =>
    sliceUUIDFromUserName(displayName).split(' ')[0];

interface Props {
    /** ISO-8601 scheduled start, if this meeting was booked ahead of time (VID-436). */
    scheduledStart?: string;
    /** ISO-8601 scheduled end, if this meeting was booked ahead of time (VID-436). */
    scheduledEnd?: string;
}

const MeetingLobbyOngoingStatus = ({ scheduledStart, scheduledEnd }: Props) => {
    const { i18next } = useAppContext();

    const lobbyMembers = useAppSelector(selectLobbyMembers);

    const hasMembers = !!lobbyMembers?.size;
    const sortedMembers = hasMembers
        ? lobbyMembers.sortBy((member) => member.get('joinTime'))
        : null;
    const startedAt = sortedMembers?.first()?.get('joinTime');

    const minutesUntilStart = scheduledStart
        ? moment(scheduledStart).diff(moment(), 'minutes')
        : null;
    // Presence overrides the schedule: once anyone has actually joined, the
    // meeting counts as started even if the scheduled start hasn't arrived.
    const beforeStart =
        !hasMembers && !!minutesUntilStart && minutesUntilStart > 0;
    const hasEnded =
        !!scheduledEnd &&
        !beforeStart &&
        moment().isAfter(moment(scheduledEnd));

    // Only the clock can move the label on from the countdown, or from ongoing
    // to ended. Past both, it stops (the "Ongoing" elapsed timer keeps itself
    // ticking separately).
    const labelCanChangeWithTime = beforeStart || (!!scheduledEnd && !hasEnded);
    useSharedInterval(
        labelCanChangeWithTime ? COUNTDOWN_REFRESH_INTERVAL_MS : null,
    );

    const isNotStarted = !scheduledStart && !hasMembers;

    // The bold status label on the left. Left `null` for the "Ongoing" state,
    // which renders its own live elapsed timer instead of static text.
    const statusLabel = (() => {
        if (isNotStarted) {
            return i18next.t('videoconference.lobby.not_started');
        }
        if (beforeStart) {
            return minutesUntilStart > PLANNED_VS_COUNTDOWN_THRESHOLD_MINUTES
                ? i18next.t('videoconference.lobby.meeting_planned_for', {
                      time: timeFormater(scheduledStart),
                  })
                : i18next.t('videoconference.lobby.meeting_starts_in', {
                      count: minutesUntilStart,
                  });
        }
        if (hasEnded) {
            return i18next.t('videoconference.lobby.meeting_ended_at', {
                time: timeFormater(scheduledEnd),
            });
        }
        return null;
    })();

    // Nobody present before the scheduled start renders no participants copy
    // at all; otherwise it's either "be the first to join" or the name list.
    const participantsSummary = (() => {
        if (beforeStart) return null;

        if (!hasMembers) {
            return i18next.t('videoconference.lobby.be_the_first_to_join');
        }

        const names = sortedMembers
            .map((member) => firstName(member.get('displayName')))
            .toArray();

        if (names.length === 1) {
            return i18next.t('videoconference.lobby.member_is_here', {
                name: names[0],
            });
        }
        if (names.length === 2) {
            return i18next.t('videoconference.lobby.two_members_are_here', {
                name1: names[0],
                name2: names[1],
            });
        }
        return i18next.t('videoconference.lobby.members_and_more_are_here', {
            name1: names[0],
            name2: names[1],
            count: names.length - 2,
        });
    })();

    if (participantsSummary === null) {
        return (
            <>
                <Divider className="my-0" />
                <div className="px-2 py-1 text-sm text-gray-800">
                    {statusLabel}
                </div>
            </>
        );
    }

    return (
        <>
            <Divider className="my-0" />

            <div className="flex items-center justify-between gap-4 px-2 py-1">
                <div className="text-gray-800">
                    {statusLabel === null ? (
                        <>
                            <span className="font-bold">
                                {i18next.t('videoconference.lobby.ongoing')}
                            </span>{' '}
                            <ElapsedTime since={scheduledStart ?? startedAt} />
                        </>
                    ) : (
                        <span className="font-bold">{statusLabel}</span>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    <div className="text-sm text-gray-800">
                        {participantsSummary}
                    </div>
                    {hasMembers && (
                        <div className="flex">
                            {sortedMembers
                                .slice(0, MAX_STACKED_AVATARS)
                                .map((member, index) => (
                                    <div
                                        key={member.get('key')}
                                        className={twMerge(
                                            'rounded-full ring-2 ring-white',
                                            index > 0 && '-ms-2',
                                        )}
                                    >
                                        <MeetingLobbyOngoingAvatar
                                            member={member}
                                        />
                                    </div>
                                ))
                                .toArray()}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default MeetingLobbyOngoingStatus;
